import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GuidedCaseLayout, type GuidedStep } from "@/components/radnici/GuidedCaseLayout";
import { CaseCard } from "@/components/radnici/ProcessCaseUI";
import { InvoiceAllocationEditor, ManualInvoiceBasis } from "@/components/nabava/InvoiceAllocationEditor";
import { errorMessage } from "@/lib/api";
import { datum } from "@/lib/format";
import { hasProcurement, quantity } from "@/lib/procurement";
import { invoiceMoney as money } from "@/lib/receivedInvoices";
import {
  basisReason,
  hasInvoicePermission,
  invoiceStates,
  receivedInvoices,
  splitReason,
  writeStates,
  type Invoice,
  type InvoiceComparison,
  type InvoiceControl,
  type InvoiceSplit,
  type InvoiceWrite,
} from "@/lib/receivedInvoices";
import "@/components/nabava/ReceivedInvoices.css";
import { InvoiceNavigation } from "@/components/nabava/InvoiceNavigation";

interface ScreenData {
  invoice: Invoice;
  control: InvoiceControl;
  comparison: InvoiceComparison;
  outbox: InvoiceWrite[];
  splitWarning: string;
}
export function ReceivedInvoicePage() {
  const { id = "" } = useParams(),
    { user } = useAuth(),
    navigate = useNavigate();
  const [data, setData] = useState<ScreenData | null>(null),
    [split, setSplit] = useState<InvoiceSplit[]>([]);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [dirtyBasis, setDirtyBasis] = useState(false);
  const [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [blockReason, setBlockReason] = useState("");
  const [help, setHelp] = useState("");
  const read = hasInvoicePermission(user),
    liquidate = hasInvoicePermission(user, "likvidiraj"),
    approve = hasInvoicePermission(user, "odobri_placanje");
  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const [invoice, control, comparison, outbox, proposed] = await Promise.all([
          receivedInvoices.detail(id, signal),
          receivedInvoices.control(id, signal),
          receivedInvoices.comparison(id, signal),
          receivedInvoices.outbox(id, signal),
          receivedInvoices
            .split(id, signal)
            .then((rows) => ({ rows, warning: "" }))
            .catch((cause) => ({
              rows: [] as InvoiceSplit[],
              warning: errorMessage(cause, "Prijedlog raščlambe nije dostupan."),
            })),
        ]);
        if (!signal?.aborted) {
          setData({ invoice, control, comparison, outbox, splitWarning: proposed.warning });
          setSplit(proposed.rows);
          setDirtyBasis(false);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [id],
  );
  useEffect(() => {
    if (!read) return;
    const controller = new AbortController();
    Promise.resolve()
      .then(() => {
        if (controller.signal.aborted) return;
        setData(null);
        setError("");
        setMessage("");
        setHelp("");
        return load(controller.signal);
      })
      .catch((cause) => {
        if (!controller.signal.aborted) setError(errorMessage(cause, "Podaci računa nisu učitani."));
      });
    return () => controller.abort();
  }, [load, read]);
  async function refresh() {
    setError("");
    try {
      await load();
    } catch (cause) {
      setError(errorMessage(cause, "Podaci računa nisu učitani."));
    }
  }
  if (!read) return <Navigate to="/" replace />;
  const back = (
    <Button variant="secondary" onClick={() => navigate("/nabava/ulazni-racuni")}>
      Svi ulazni računi
    </Button>
  );
  if (!data || data.invoice.id !== id)
    return (
      <div className="invoice-screen space-y-4 py-4">
        {back}
        {loading && <p role="status">Učitavanje računa…</p>}
        {error && <p role="alert">{error}</p>}
        <Button variant="secondary" disabled={loading} onClick={() => void refresh()}>
          Ponovno učitaj
        </Button>
      </div>
    );
  const { invoice, control, comparison, outbox } = data;
  const preLiquidation = ["za_provjeru", "nepovezano", "blokirano"].includes(invoice.stanje);
  const draft = invoice.status === "O",
    editable = liquidate && preLiquidation && draft && !loading && !busy;
  const open = control.differences.filter((row) => row.stanje !== "rijeseno");
  const linked = control.orders.length > 0;
  const reason = busy
    ? "Spremanje je u tijeku."
    : loading
      ? "Pričekajte osvježavanje svih podataka."
      : error
        ? "Podaci nisu pouzdano osvježeni. Ponovno učitajte račun."
        : !draft
          ? "Račun više nije osnutak u Minimaxu."
          : !linked
            ? "Račun nije povezan s narudžbom."
            : !control.receipt_confirmed
              ? "Nedostaje potvrđeni primitak robe ili izvršenje usluge."
              : open.length
                ? "Račun ima otvorene blokirajuće D8 razlike."
                : dirtyBasis
                  ? "Najprije spremite osnovicu i PDV."
                  : invoice.amount_net === null || invoice.vat_amount === null
                    ? "Nedostaje neto osnovica računa i PDV."
                    : basisReason(invoice.amount_net, invoice.vat_amount, invoice.amount) ||
                      invoice.check_reason ||
                      (invoice.currency !== "EUR" ? "Likvidacija zahtijeva iznos u EUR." : "") ||
                      (comparison.has_difference
                        ? comparison.reasons[0] || "Usporedba računa, narudžbe i primitka ima neriješene razlike."
                        : "") ||
                      splitReason(split, invoice.amount_net);
  const waiting = ["ceka_upravu", "ceka_placanje"].includes(invoice.stanje);
  const canAct = preLiquidation ? liquidate : waiting ? approve : false;
  const actionReason =
    reason ||
    (!canAct
      ? waiting
        ? "Nemate ovlast odobrenja plaćanja."
        : preLiquidation
          ? "Nemate ovlast likvidacije računa."
          : "Kontrola u AVM-u je završena; potvrda i knjiženje obavljaju se u Minimaxu."
      : "");
  const primaryLabel = preLiquidation
    ? approve && liquidate
      ? "Likvidiraj i odobri plaćanje"
      : "Likvidiraj i pošalji upravi"
    : waiting
      ? "Odobri plaćanje"
      : "Čeka potvrdu u Minimaxu";
  async function act(kind: "management" | "combined" | "approve" | "block") {
    if (busy || loading) return;
    if (kind !== "block" && actionReason) return;
    setBusy(true);
    setError("");
    setMessage("");
    let liquidated = false;
    try {
      if (kind === "block") {
        await receivedInvoices.block(
          invoice.id,
          blockReason.trim(),
          open.map((row) => row.id),
        );
        setMessage("Račun je blokiran. Napomena „Ne plaćati” stavljena je u red upisa.");
      } else {
        if (kind !== "approve") {
          await receivedInvoices.liquidate(invoice, split.map((line) => ({
            ...line,
            mjesto_troska: control.sites.find((site) => site.id === line.gradiliste_id)?.mjesto_troska || null,
          })));
          liquidated = true;
        }
        if (kind === "approve" || kind === "combined") await receivedInvoices.approve(invoice.id);
        setMessage(
          kind === "management"
            ? "Račun je likvidiran i čeka odobrenje uprave."
            : "Plaćanje je odobreno. Aplikacija ne izvršava plaćanje; provjerite red upisa prije potvrde u Minimaxu.",
        );
      }
      await load();
    } catch (cause) {
      const failure = errorMessage(cause, "Radnja nije dovršena.");
      try {
        await load();
      } catch {
        setData(null);
      }
      setError(
        liquidated && kind === "combined"
          ? `Račun je likvidiran i čeka upravu; odobrenje plaćanja nije dovršeno. ${failure}`
          : failure,
      );
    } finally {
      setBusy(false);
    }
  }
  const blockDisabled = busy || loading || !draft || !linked || !open.length || !blockReason.trim();
  const blockDisabledReason =
    busy || loading
      ? "Pričekajte završetak radnje."
      : !draft
        ? "Račun više nije osnutak u Minimaxu."
        : !linked
          ? "Nedostaje povezana narudžba."
          : !open.length
            ? "Za blokadu treba otvorena D8 razlika povezanog računa."
            : !blockReason.trim()
              ? "Upišite razlog blokade."
              : "";
  const nextHelp = (text: string) => (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={() => setHelp(text)}>
        Provjeri
      </Button>
      {hasProcurement(user, "read") &&
        control.orders.map((order) => (
          <Button key={order.id} variant="secondary" onClick={() => navigate(`/nabava/${order.id}`)}>
            Otvori {order.broj || "narudžbu"}
          </Button>
        ))}
    </div>
  );
  const steps: GuidedStep[] = [
    { id: "import", name: "Račun preuzet iz Minimaxa", state: "completed", closedOn: invoice.synced_at },
    { id: "control", name: "Provjera i likvidacija", state: preLiquidation ? "current" : "completed" },
    {
      id: "approval",
      name: "Odobrenje plaćanja",
      state: waiting ? "current" : preLiquidation ? "future" : "completed",
      description: "Nakon likvidacije ovlaštena osoba odobrava plaćanje.",
    },
    {
      id: "minimax",
      name: "Potvrda u Minimaxu",
      state:
        invoice.stanje === "potvrdeno_u_minimaxu"
          ? "completed"
          : invoice.stanje === "odobreno_placanje"
            ? "current"
            : "future",
      description: "Nakon odobrenja provjerite upis i potvrdite osnutak u Minimaxu.",
    },
  ];
  const latest = outbox.filter((row) => row.liquidation_version === invoice.liquidation_version).at(-1);
  return (
    <div className="invoice-screen space-y-4 py-4">
      <InvoiceNavigation />
      {invoice.stanje === "za_provjeru" && invoice.block_reason && <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3">Vraćeno Nikolini: {invoice.block_reason}</p>}
      <div className="flex flex-wrap gap-3">
        {back}
        <Button variant="secondary" disabled={loading || busy} onClick={() => void refresh()}>
          Osvježi račun
        </Button>
      </div>
      {error && (
        <p role="alert" className="rounded-lg border border-red-400 p-3">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="rounded-lg border border-green-400 p-3">
          {message}
        </p>
      )}
      <GuidedCaseLayout
        number={invoice.number}
        kind="Ulazni račun"
        person={invoice.supplier_name}
        owner="nabava / uprava"
        summary={invoiceStates[invoice.stanje] || invoice.stanje}
        tone={
          invoice.stanje === "blokirano" || comparison.has_difference
            ? "bad"
            : invoice.stanje === "odobreno_placanje"
              ? "good"
              : "warn"
        }
        summaryBadge={false}
        signals={[]}
        steps={steps}
        stepOrder="sequential"
        omitMissingAttribution
        currentTitle="Sljedeći korak"
        instructions={
          preLiquidation
            ? "Provjerite primitak, D8 razlike i neto raščlambu prije likvidacije."
            : waiting
              ? "Ovlaštena osoba odobrava plaćanje provjerenog računa."
              : "Provjerite napomenu i stanje upisa. Račun se potvrđuje u Minimaxu."
        }
        checklist={[
          {
            id: "linked",
            label: "Povezano s narudžbom",
            ready: linked,
            detail: control.orders.map((order) => order.broj || "Bez NAR broja").join(", "),
            actions: nextHelp(
              linked
                ? "Povezane narudžbe prikazane su u podacima računa i usporedbi."
                : "Upišite točan NAR broj u referencu osnutka u Minimaxu. Povezivanje se provjerava pri sljedećoj sinkronizaciji.",
            ),
          },
          {
            id: "receipt",
            label: "Primitak potvrđen",
            ready: control.receipt_confirmed,
            actions: nextHelp(
              "Primitak ili izvršenje usluge potvrđuje se na povezanoj narudžbi. Ispod su prikazani stvarno potvrđeni primitci.",
            ),
          },
          {
            id: "differences",
            label: "D8 razlike riješene",
            ready: linked && !open.length,
            detail: open.length
              ? `${open.length} otvorenih razlika`
              : linked
                ? "Nema otvorenih D8 razlika."
                : "Najprije povežite račun.",
            actions: nextHelp(
              open.length
                ? "Otvorene razlike riješite na povezanim narudžbama. Blokada ih zadržava povezanima s računom."
                : "Nema otvorenih D8 razlika na povezanim narudžbama.",
            ),
          },
        ]}
        currentContent={
          <>
            {help && <p role="status">{help}</p>}
            {actionReason && (
              <p id="invoice-action-reason" className="text-red-800">
                {actionReason}
              </p>
            )}
            {open.length > 0 && (
              <ul>
                {open.map((row) => (
                  <li key={row.id}>
                    D8 · {row.vrsta} · {row.stanje}
                  </li>
                ))}
              </ul>
            )}
          </>
        }
        primaryAction={
          (canAct || (!preLiquidation && !waiting)) && (
            <Button
              disabled={!!actionReason}
              aria-describedby={actionReason ? "invoice-action-reason" : undefined}
              onClick={() => void act(waiting ? "approve" : approve ? "combined" : "management")}
            >
              {primaryLabel}
            </Button>
          )
        }
        secondaryActions={
          <div className="space-y-3">
            {preLiquidation && liquidate && approve && (
              <Button
                variant="secondary"
                disabled={!!actionReason}
                aria-describedby="invoice-action-reason"
                onClick={() => void act("management")}
              >
                Likvidiraj i pošalji upravi
              </Button>
            )}
            {liquidate && draft && (
              <div className="space-y-2">
                <label>
                  Razlog blokade
                  <textarea
                    maxLength={2000}
                    disabled={busy || loading}
                    value={blockReason}
                    onChange={(event) => setBlockReason(event.target.value)}
                  />
                </label>
                <p className="text-sm">Blokada povezuje sve gore navedene otvorene D8 razlike.</p>
                <Button
                  variant="destructive"
                  disabled={blockDisabled}
                  aria-describedby="invoice-block-reason"
                  onClick={() => void act("block")}
                >
                  Blokiraj
                </Button>
                {blockDisabledReason && <p id="invoice-block-reason">{blockDisabledReason}</p>}
              </div>
            )}
          </div>
        }
        metadataOpen
        metadata={
          <div className="space-y-4">
            <dl className="invoice-facts">
              <dt>Dobavljač</dt>
              <dd>{invoice.supplier_name}</dd>
              <dt>Broj</dt>
              <dd>{invoice.number}</dd>
              <dt>Datum / dospijeće</dt>
              <dd>
                {datum(invoice.date_issued)} / {datum(invoice.date_due)}
              </dd>
              <dt>Narudžbe</dt>
              <dd>{control.orders.map((order) => order.broj).join(", ") || "—"}</dd>
              <dt>Gradilišta</dt>
              <dd>{control.sites.map((site) => site.naziv).join(", ") || "—"}</dd>
              <dt>Osnovica</dt>
              <dd>{money(invoice.amount_net)}</dd>
              <dt>PDV</dt>
              <dd>{money(invoice.vat_amount)}</dd>
              <dt>Iznos računa</dt>
              <dd>
                {money(invoice.amount)} ({invoice.currency})
              </dd>
              <dt>Izvor osnovice</dt>
              <dd>
                {invoice.osnovica_izvor === "ubl"
                  ? "UBL"
                  : invoice.osnovica_izvor === "rucno"
                    ? "Ručno"
                    : "Nije upisana"}
              </dd>
            </dl>
            <section>
              <h2 className="font-semibold">Što ide u Minimax</h2>
              {latest ? (
                <>
                  <p>{writeStates[latest.stanje] || latest.stanje}</p>
                  <pre className="whitespace-pre-wrap break-words text-sm">{latest.notes}</pre>
                  {latest.zadnja_greska && <p role="status">{latest.zadnja_greska}</p>}
                  {latest.stanje === "nepodrzano" && <p>Napomenu i neto raščlambu unesite ručno u Minimax.</p>}
                </>
              ) : (
                <>
                  <p>
                    {outbox.length
                      ? "Prethodni upis više ne odgovara aktualnoj verziji računa. Potrebna je ponovna kontrola."
                      : "Predložak napomene prije likvidacije (još nije stavljen u red upisa):"}
                  </p>
                  <p className="whitespace-pre-wrap text-sm">
                    {invoice.stanje === "blokirano"
                      ? `Ne plaćati — ${invoice.block_reason || "blokirano"}`
                      : "Likvidirano — čeka odobrenje uprave"}
                    {split
                      .map(
                        (line) =>
                          `\n${line.konto} · ${control.sites.find((site) => site.id === line.gradiliste_id)?.naziv || "—"} · ${line.mjesto_troska || "—"} · ${money(line.iznos)}`,
                      )
                      .join("")}
                  </p>
                </>
              )}
              <p className="text-sm mt-2">U osnutak idu napomena i analitika. Potvrda i knjiženje ostaju u Minimaxu.</p>
              <Button
                variant="secondary"
                onClick={() => {
                  if (!navigator.clipboard) {
                    setMessage("Kopiranje nije dostupno. Broj prepišite iz podataka računa.");
                    return;
                  }
                  void navigator.clipboard.writeText(invoice.number).then(
                    () => setMessage("Broj računa je kopiran."),
                    () => setMessage("Kopiranje nije dostupno. Broj prepišite iz podataka računa."),
                  );
                }}
              >
                Kopiraj broj računa
              </Button>
            </section>
          </div>
        }
        diary={
          <ul className="space-y-2 text-sm">
            <li>{new Date(invoice.synced_at).toLocaleString("hr-HR")} · Zadnje preuzimanje iz Minimaxa</li>
            {invoice.osnovica_izvor === "rucno" && <li>Osnovica i PDV uneseni su ručno.</li>}
            {invoice.stanje === "za_provjeru" && invoice.block_reason && <li>Vraćeno Nikolini na provjeru: {invoice.block_reason}</li>}
            {outbox.map((row) => (
              <li key={row.id}>
                {new Date(row.created_at).toLocaleString("hr-HR")} · {writeStates[row.stanje] || row.stanje} ·{" "}
                {row.notes?.split("\n")[0]}
                {row.zadnja_greska && ` · ${row.zadnja_greska}`}
              </li>
            ))}
          </ul>
        }
      >
        <CaseCard title="Račun prema narudžbi i primitku">
          <InvoiceComparisonTable comparison={comparison} />
          {comparison.reasons.map((reason) => (
            <p key={reason} className="text-red-800">
              {reason}
            </p>
          ))}
          {control.receipts.length > 0 && (
            <div className="invoice-table-wrap">
              <table>
                <caption className="text-left font-semibold py-2">Potvrđeni primitci / otpremnice</caption>
                <thead>
                  <tr>
                    <th>Otpremnica</th>
                    <th>NAR</th>
                    <th>Gradilište</th>
                    <th>Stavka</th>
                    <th>Količina</th>
                    <th>Bez PDV-a</th>
                  </tr>
                </thead>
                <tbody>
                  {control.receipts.map((row, index) => (
                    <tr key={`${row.id}:${index}`}>
                      <td>{row.broj_otpremnice || "Bez otpremnice"}</td>
                      <td>{row.narudzba}</td>
                      <td>{row.gradiliste}</td>
                      <td>{row.name}</td>
                      <td>
                        {quantity(row.quantity)} {row.unit}
                      </td>
                      <td>{money(row.amount_net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CaseCard>
        {editable && invoice.osnovica_izvor !== "ubl" && (
          <CaseCard title="Ručni unos osnovice i PDV-a">
            <ManualInvoiceBasis
              key={`${invoice.id}:${invoice.version}`}
              invoice={invoice}
              disabled={busy || loading}
              onDirty={setDirtyBasis}
              onSaved={() => load()}
            />
          </CaseCard>
        )}
        <CaseCard title="Raščlamba za knjiženje">
          <InvoiceAllocationEditor
            split={split}
            onChange={setSplit}
            net={invoice.amount_net}
            control={control}
            editable={editable}
          />
          {data.splitWarning && (
            <p className="text-sm">Prijedlog nije dostupan: {data.splitWarning} Unesite raščlambu ručno.</p>
          )}
        </CaseCard>
      </GuidedCaseLayout>
    </div>
  );
}

function signedMoney(value: string | null) {
  return value?.startsWith("-") ? `−${money(value.slice(1))}` : money(value);
}
function different(value: string | null) {
  return value !== null && !/^[-+]?0+(?:\.0+)?$/.test(value);
}
export function InvoiceComparisonTable({ comparison }: { comparison: InvoiceComparison }) {
  return (
    <div className="invoice-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Stavka</th>
            <th>Naručeno</th>
            <th>Stiglo</th>
            <th>Na računu</th>
            <th>Neto iznos računa</th>
            <th>Razlika prema primitku</th>
          </tr>
        </thead>
        <tbody>
          {comparison.mode === "items" ? (
            comparison.lines.map((line, index) => (
              <tr
                key={index}
                className={
                  [
                    line.quantity_difference,
                    line.receipt_difference,
                    line.amount_difference,
                    line.receipt_amount_difference,
                  ].some(different)
                    ? "has-difference"
                    : ""
                }
              >
                <td>{line.name}</td>
                <td>
                  {quantity(line.ordered)} {line.unit}
                </td>
                <td>
                  {quantity(line.received)} {line.unit}
                </td>
                <td>
                  {quantity(line.invoiced)} {line.unit}
                </td>
                <td>{money(line.invoiced_net)}</td>
                <td>{signedMoney(line.receipt_amount_difference)}</td>
              </tr>
            ))
          ) : (
            <tr className={comparison.has_difference ? "has-difference" : ""}>
              <td>Ukupno bez UBL stavaka</td>
              <td>{money(comparison.expected_net)}</td>
              <td>{money(comparison.received_net)}</td>
              <td>—</td>
              <td>{money(comparison.amount_net)}</td>
              <td>{signedMoney(comparison.receipt_amount_difference)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
